/**
 * @file Rendu conditionnel du contenu de chaque étape du widget de réservation.
 * @module packages.common.components.table-reservation.utils.stepContentRenderer
 *
 * Mappe l'étape courante vers le composant React correspondant.
 * Les étapes conditionnelles (paiement, formules) sont chargées en lazy loading
 * via React.lazy pour réduire la taille du bundle initial.
 */

import React, {lazy, Suspense} from 'react';
import {ServiceGuestsStep} from '../sections/ServiceGuestsStep';
import {DateStep} from '../sections/DateStep';
import {RoomStep} from '../sections/RoomStep';
import {TimeStep} from '../sections/TimeStep';
import {ContactStep} from '../sections/ContactStep';
import {AdditionalDetailsStep} from '../sections/AdditionalDetailsStep';
import {ConfirmationStep} from '../sections/ConfirmationStep';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

const PaymentStep = lazy(() => import('../sections/PaymentStep'));
const SlotLostPanel = lazy(() => import('../sections/SlotLostPanel'));
const FormulaStep = lazy(() => import('../sections/FormulaStep'));

/**
 * Rend le composant correspondant à l'étape courante du parcours de réservation.
 *
 * @param {Object} params - Paramètres du rendu
 * @param {number} params.currentStep - Étape logique courante (valeur de STEPS)
 * @param {Object} params.STEPS - Enum des étapes logiques
 * @param {Object} params.formData - Données du formulaire
 * @param {Function} params.updateFormData - Setter de champ du formulaire
 * @param {Object} params.config - Configuration du restaurant
 * @param {Object} params.availableSteps - Étapes disponibles et mapping
 * @param {Function} [params.onCardInputError] - Enregistreur d'erreur de saisie de carte (signal empreinte)
 * @param {Function} [params.handleChooseAnotherDate] - Retour au calendrier après un créneau perdu
 * @param {Function} params.t - Fonction de traduction générique
 * @param {Function} params.getCustomText - Fonction de traduction des textes personnalisés
 * @return {JSX.Element|null} Composant de l'étape ou null si non disponible
 */
export function renderStepContent({
  currentStep,
  STEPS,
  formData,
  updateFormData,
  config,
  validationErrors,
  provideValidationFunction,
  handleSetErrors,
  setIsStepReady,
  handleComplete,
  handleReset,
  handleCloseWidget,
  handleNextStep,
  companyId,
  displayMode,
  embedded,
  availableSteps,
  primaryColor,
  companyDetails,
  prepareError,
  onCardInputError,
  handleChooseAnotherDate,
  onContentResize,
  t,
  getCustomText,
}) {
  const commonProps = {
    formData,
    updateFormData,
    config,
    setValidation: provideValidationFunction,
    setErrors: handleSetErrors,
    errors: validationErrors,
    setStepReady: setIsStepReady,
    primaryColor,
    companyDetails,
    onContentResize,
    t,
    getCustomText,
  };

  const currentStepExists = currentStep === STEPS.SERVICE_GUESTS ||
                           currentStep === STEPS.CONFIRMATION ||
                           availableSteps.mapping[currentStep] !== undefined;

  if (!currentStepExists) {
    return null;
  }

  // Créneau perdu après le choix (service complet entre-temps, blocage) : l'écran
  // dédié remplace l'étape en cours, avec ou sans empreinte, jusqu'au retour au
  // calendrier. Placé avant le stepper pour qu'aucune étape ne reste sans issue.
  if (formData.slotLost && currentStep !== STEPS.CONFIRMATION) {
    return (
      <Suspense fallback={null}>
        <SlotLostPanel
          slotLost={formData.slotLost}
          formData={formData}
          onChooseAnotherDate={handleChooseAnotherDate}
          primaryColor={primaryColor}
          t={t}
        />
      </Suspense>
    );
  }

  switch (currentStep) {
    case STEPS.SERVICE_GUESTS:
      return <ServiceGuestsStep {...commonProps} />;

    case STEPS.DATE:
      return <DateStep {...commonProps} companyId={companyId} />;

    case STEPS.ROOM:
      return <RoomStep {...commonProps} onNextStep={handleNextStep} />;

    case STEPS.TIME:
      return <TimeStep {...commonProps} companyId={companyId} />;

    case STEPS.FORMULA:
      return formData.formulasRequired ? (
        <Suspense
          fallback={
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          }
        >
          <FormulaStep {...commonProps} onNextStep={handleNextStep} />
        </Suspense>
      ) : null;

    case STEPS.CONTACT:
      return <ContactStep {...commonProps} onNextStep={handleNextStep} />;

    case STEPS.DETAILS:
      return (
        <AdditionalDetailsStep
          {...commonProps}
          onComplete={handleComplete}
        />
      );

    case STEPS.PAYMENT:
      return formData.paymentRequired ? (
        <Suspense
          fallback={
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          }
        >
          <PaymentStep
            {...commonProps}
            setIsStepReady={setIsStepReady}
            provideValidationFunction={provideValidationFunction}
            handleSetErrors={handleSetErrors}
            errors={validationErrors}
            prepareError={prepareError}
            onCardInputError={onCardInputError}
          />
        </Suspense>
      ) : null;

    case STEPS.CONFIRMATION:
      return (
        <ConfirmationStep
          formData={formData}
          config={config}
          onReset={handleReset}
          onClose={handleCloseWidget}
          embedded={embedded}
          displayMode={displayMode}
          primaryColor={primaryColor}
          t={t}
        />
      );

    default:
      return null;
  }
}
